import Konva from 'konva';
import LabelledCircle from "./LabelledCircle.js";
import WrappedArrow from "./WrappedArrow.js";

const EPS = 1e-6;

export default class EventView extends Konva.Group {
  constructor(nBeats, pixelsPerBeat, stage, snapManager, onThrowChange, onDoubleClick) {
    super({position: {x: 0, y: 0}});
    this._nBeats = nBeats || 0;
    this._pixelsPerBeat = pixelsPerBeat || 0;
    this._arrowMidpoints = [];

    this.stage = stage;
    this.snapManager = snapManager;
    this.snapManager.addSnappableObject(this);
    this.onDoubleClick = onDoubleClick;
    this.onThrowChange = onThrowChange;

    this.initializeVisuals();
  }

  initializeVisuals() {
    this.circle = new LabelledCircle({
      radius: 30,
      fill: "white",
      stroke: "black",
      strokeWidth: 4,
      text: "",
      fontSize: 20,
      visible: true,
      draggable: true,
      dragBoundFunc: () => { return this.circle.absolutePosition(); }, // Have the circle stay still when dragging
    });

    this.arrow = new WrappedArrow({
      position: { x: 0, y: 0 },
      visible: true,
      viewWidth: this.nBeats() * this.pixelsPerBeat(),
    });

    this.add(this.circle);
    this.add(this.arrow);
    this.arrow.zIndex(0);
    this.circle.zIndex(1);
    this.circle.on("dragend", () => this.dragend());
    this.circle.on("dragmove", () => this.dragmove());
    this.circle.on("dblclick dbltap", () => this.onDoubleClick(this));
  }

  hand(hand) {
    this.circle.fill(hand ? "white" : "grey");
  }

  label(label) {
    this.circle.text(label);
  }

  valid(valid) {
    this.circle.stroke(valid ? "black" : "red");
  }

  pixelsPerBeat(pixelsPerBeat) {
    if (!pixelsPerBeat) {
      return this._pixelsPerBeat;
    }
    this._pixelsPerBeat = pixelsPerBeat;
    this.arrow.viewWidth(this.viewWidth());
  }

  nBeats(nBeats) {
    if (!nBeats) {
      return this._nBeats;
    }
    this._nBeats = nBeats;
    this.arrow.viewWidth(this.viewWidth());
  }

  viewWidth() {
    return this.nBeats() * this.pixelsPerBeat();
  }

  position(position) {
    if (!position) {
      return this.circle.position();
    }
    const positionDelta = {x: position.x - this.circle.position().x, y: position.y - this.circle.position().y};
    const newPoints = this.arrow.points().map(point => ({x: point.x + positionDelta.x, y: point.y + positionDelta.y}));
    this.arrow.points(newPoints);
    return this.circle.position(position);
  }

  getArrowPoints(from, to) {
    const points = [];
    points.push({x: from.x, y: from.y});
    for (const midpoint of this._arrowMidpoints) {
      points.push(midpoint);
    }
    points.push({x: to.x, y: to.y});
    return points;
  }

  setArrowMidpoints(midpoints) {
    this._arrowMidpoints = midpoints;
  }

  getCircleEdge(start, end) {
    // When we are snapped to an event, we want to draw the arrow to the edge of the circle.
    const DIST = 35;
    let ratio = 1 - DIST / Math.hypot(end.x - start.x, end.y - start.y);
    return {
      x: start.x + (end.x - start.x) * ratio,
      y: start.y + (end.y - start.y) * ratio,
    };
  }

  getArrowEnd(from, point) {
    const wraps = Math.floor(point.x / this.viewWidth());
    const wrapped = {x: point.x % this.viewWidth(), y: point.y};
    const snapped = this.snapManager.findSnapTarget(wrapped);
    if (snapped) {
      // const newpoint = snapped.position();
      // if (point.x >= this.viewWidth()) {
      //   newpoint.x -= this.viewWidth();
      // }
      // point = newpoint;
      point = snapped.position();
      point.x += this.viewWidth() * wraps;
    }
    if (point.x + 2 * this.pixelsPerBeat() < from.x - EPS) {
      point.x += this.viewWidth();
    }
    if (snapped) {
      point = this.getCircleEdge(from, point);
    }
    return {from, to: point, snapped: snapped};
  }

  setEndPoint(point, onlyShowSnapped = false) {
    const from = this.circle.position();
    const end = this.getArrowEnd(from, point);
    this.arrowEnd = end.to;
    this.snappedLoc = end.snapped && end.snapped.position();
    const points = this.getArrowPoints(from, end.to);
    this.arrow.points(points);
    this.arrow.visible(end.snapped !== undefined || !onlyShowSnapped);
  }

  dragend() {
    this.setEndPoint(this.stage.getPointerPosition(), true);
    this.onThrowChange({from: this.circle.position(), to: this.arrowEnd, snapped: this.snappedLoc});
  }

  dragmove() {
    this._arrowMidpoints = [];
    this.setEndPoint(this.stage.getPointerPosition());
    this.stage.clear();
    this.stage.draw();
  }
}
